import type { Problem, StaffTokens } from './types';

/**
 * API origin: the container's runtime config (`/config.js`, from `PLAYBOX_API_BASE_URL`), else
 * the build-time `VITE_API_BASE_URL`. Empty in dev (the Vite proxy serves /v1 and /dev
 * same-origin).
 */
export const API_BASE_URL: string = (
  (typeof window !== 'undefined' ? window.__PLAYBOX_CONFIG__?.apiBaseUrl : undefined) ||
  import.meta.env.VITE_API_BASE_URL ||
  ''
).replace(/\/$/, '');

/** Absolute origin for requests: the configured API origin, else this page's origin. */
export function apiOrigin(): string {
  if (API_BASE_URL) return API_BASE_URL;
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
}

export const ADMIN_HEADER = { 'X-Playbox-Admin': '1' } as const;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string | null;
  readonly problem: Problem;

  constructor(status: number, problem: Problem) {
    super(problem.detail || problem.title || `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = problem.code ?? 'unknown';
    this.detail = problem.detail ?? null;
    this.problem = problem;
  }
}

/** Human-readable text for any thrown value: problem+json `detail` first. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0)
      return "Couldn't reach the Playbox API. Check your connection and try again.";
    return err.detail || err.problem.title || `Request failed (${err.status}).`;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}

export function isConflict(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 409 || err.status === 412);
}

export async function toApiError(res: Response): Promise<ApiError> {
  let problem: Problem = { status: res.status, title: res.statusText };
  try {
    const text = await res.text();
    if (text) {
      const parsed: unknown = JSON.parse(text);
      if (parsed && typeof parsed === 'object') problem = { ...problem, ...(parsed as Problem) };
    }
  } catch {
    // Not JSON: keep the status line.
  }
  return new ApiError(res.status, problem);
}

// ---------- session (access token in memory only) ----------

type SessionListener = (tokens: StaffTokens | null) => void;

let current: StaffTokens | null = null;
let expiresAtMs = 0;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let inflightRefresh: Promise<StaffTokens | null> | null = null;
const listeners = new Set<SessionListener>();

/** Refresh this long before the access token expires. */
export const REFRESH_LEEWAY_MS = 60_000;

export function getAccessToken(): string | null {
  return current?.access_token ?? null;
}

export function getSession(): StaffTokens | null {
  return current;
}

export function onSessionChange(fn: SessionListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setSession(tokens: StaffTokens | null): void {
  current = tokens;
  clearTimeout(refreshTimer);
  refreshTimer = undefined;
  if (tokens) {
    const lifetimeMs = Math.max(0, tokens.expires_in * 1000);
    expiresAtMs = Date.now() + lifetimeMs;
    const delay = Math.max(5_000, lifetimeMs - REFRESH_LEEWAY_MS);
    refreshTimer = setTimeout(() => {
      void refreshSession().catch(() => undefined);
    }, delay);
  } else {
    expiresAtMs = 0;
  }
  listeners.forEach((fn) => fn(tokens));
}

export function accessTokenExpiresAt(): number {
  return expiresAtMs;
}

async function postAuth(path: string, body?: unknown): Promise<Response> {
  return fetch(`${apiOrigin()}/v1/admin/auth/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...ADMIN_HEADER,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/**
 * Exchange the HttpOnly refresh cookie for a new access token. Single-flight: concurrent
 * callers share one request (the refresh token rotates, so two parallel refreshes would trip
 * reuse detection and revoke the session). Resolves `null` when there is no valid session.
 */
export function refreshSession(): Promise<StaffTokens | null> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const res = await postAuth('refresh');
      if (res.ok) {
        const tokens = (await res.json()) as StaffTokens;
        setSession(tokens);
        return tokens;
      }
      if (res.status === 401 || res.status === 403 || res.status === 400) {
        setSession(null);
        return null;
      }
      throw await toApiError(res);
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

export async function login(email: string, password: string): Promise<StaffTokens> {
  const res = await postAuth('login', { email, password });
  if (!res.ok) throw await toApiError(res);
  const tokens = (await res.json()) as StaffTokens;
  setSession(tokens);
  return tokens;
}

export async function acceptInvite(token: string, name: string, password: string) {
  const res = await postAuth('accept-invite', { token, name, password });
  if (!res.ok) throw await toApiError(res);
  const tokens = (await res.json()) as StaffTokens;
  setSession(tokens);
  return tokens;
}

export async function logout(): Promise<void> {
  try {
    await postAuth('logout');
  } finally {
    setSession(null);
  }
}

/**
 * fetch() for staff-token endpoints: adds the bearer token, and on a 401 refreshes once
 * (single-flight) and retries the request once. If refresh fails the session is cleared and
 * the 401 is returned to the caller.
 */
export async function authFetch(request: Request): Promise<Response> {
  const retry = request.clone();
  const token = getAccessToken();
  if (token) request.headers.set('Authorization', `Bearer ${token}`);
  let res: Response;
  try {
    res = await fetch(request);
  } catch (e) {
    throw new ApiError(0, {
      title: 'Network error',
      detail: e instanceof Error ? e.message : null,
    });
  }
  if (res.status !== 401) return res;

  // If another request already refreshed while this one was in flight, just retry with the
  // new token; otherwise refresh (shared with any concurrent callers).
  const current = getAccessToken();
  const fresh =
    current && current !== token
      ? current
      : ((await refreshSession().catch(() => null))?.access_token ?? null);
  if (!fresh) return res;
  retry.headers.set('Authorization', `Bearer ${fresh}`);
  try {
    return await fetch(retry);
  } catch (e) {
    throw new ApiError(0, {
      title: 'Network error',
      detail: e instanceof Error ? e.message : null,
    });
  }
}
